import { api, ApiResponse } from './api';
import type { Ingrediente, CategoriaIngrediente } from '../types/ingredientes';
import type {
  MotivoSalidaBodega,
  MotivoEntradaBodega,
} from '@suminuta/shared';

// Helper para capitalizar marca
const capitalizarMarca = (marca: string | undefined): string => {
  if (!marca || marca.trim() === '') return 'Genérica';
  const trimmed = marca.trim().toLowerCase();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

// Tipos para la API (adaptadores)
export interface IngredienteAPI {
  id: number;
  nombre: string;
  marca?: string;
  descripcion?: string;
  categoria: string;
  unidad_medida: string;
  costo_unitario: number;
  stock_actual: number;
  stock_minimo: number;
  stock_maximo: number;
  proveedor?: string;
  proveedor_principal_id?: number;
  activo: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateIngredienteData {
  nombre: string;
  marca?: string;
  descripcion?: string;
  categoria: string;
  unidad_medida: string;
  costo_unitario: number;
  stock_actual?: number;
  stock_minimo?: number;
  stock_maximo: number;
  proveedor?: string;
}

export interface UpdateIngredienteData {
  nombre?: string;
  marca?: string;
  descripcion?: string;
  categoria?: string;
  unidad_medida?: string;
  costo_unitario?: number;
  stock_actual?: number;
  stock_minimo?: number;
  stock_maximo?: number;
  proveedor?: string;
}

export interface IngredientesListResponse {
  ingredientes: IngredienteAPI[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface IngredientesFilters {
  page?: number;
  limit?: number;
  search?: string;
  categoria?: string;
  activo?: boolean;
  stock_bajo?: boolean;
}

// Tipos para movimientos de stock
export interface MovimientoStock {
  id: number;
  fecha: string;
  tipo: 'entrada' | 'salida' | 'ajuste';
  motivo:
    | 'orden_compra'
    | 'produccion_cocina'
    | 'merma'
    | 'inventario'
    | 'devolucion'
    | 'salida_bodega'
    | 'reverso_salida_bodega'
    | 'entrada_bodega'
    | 'otro';
  cantidad: number;
  stock_anterior: number;
  stock_nuevo: number;
  unidad_medida: string;
  usuario: {
    id: number;
    nombre: string;
  };
  orden_compra: {
    id: number;
    numero_orden: string;
    proveedor: {
      id: number;
      nombre: string;
    } | null;
  } | null;
  receta: {
    id: number;
    nombre: string;
  } | null;
  pedido: {
    id: number;
    codigo: string;
  } | null;
  merma: {
    id: number;
  } | null;
  notas: string | null;
  lote_id: number | null;
  /** Si es una salida ya revertida, el id del movimiento que la compensa. */
  revertido_por: number | null;
  /** Si es un reverso, el id de la salida que compensa. */
  movimiento_revertido_id: number | null;
}

export interface EstadisticasMovimientos {
  total_movimientos: number;
  entradas: {
    cantidad: number;
    total: number;
  };
  salidas: {
    cantidad: number;
    total: number;
  };
  ajustes: {
    cantidad: number;
    total: number;
  };
}

export interface PuntoGrafico {
  fecha: string;
  stock: number;
  tipo: 'entrada' | 'salida' | 'ajuste';
  cantidad: number;
}

export interface MovimientosResponse {
  ingrediente: {
    id: number;
    nombre: string;
    stock_actual: number;
    unidad_medida: string;
  };
  estadisticas: EstadisticasMovimientos;
  movimientos: MovimientoStock[];
  grafico: PuntoGrafico[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface MovimientosFilters {
  page?: number;
  limit?: number;
  tipo?: 'entrada' | 'salida' | 'ajuste';
  motivo?:
    | 'orden_compra'
    | 'produccion_cocina'
    | 'merma'
    | 'inventario'
    | 'devolucion'
    | 'otro';
  fecha_inicio?: string;
  fecha_fin?: string;
}

// Mapeo de unidades de medida
const unidadesMedidaMap: Record<
  string,
  {
    nombre: string;
    abreviacion: string;
    tipo: 'peso' | 'volumen' | 'unidad' | 'otro';
  }
> = {
  kilogramos: { nombre: 'Kilogramos', abreviacion: 'kg', tipo: 'peso' },
  gramos: { nombre: 'Gramos', abreviacion: 'g', tipo: 'peso' },
  litros: { nombre: 'Litros', abreviacion: 'l', tipo: 'volumen' },
  mililitros: { nombre: 'Mililitros', abreviacion: 'ml', tipo: 'volumen' },
  unidades: { nombre: 'Unidades', abreviacion: 'u', tipo: 'unidad' },
  kg: { nombre: 'Kilogramos', abreviacion: 'kg', tipo: 'peso' },
  g: { nombre: 'Gramos', abreviacion: 'g', tipo: 'peso' },
  l: { nombre: 'Litros', abreviacion: 'l', tipo: 'volumen' },
  ml: { nombre: 'Mililitros', abreviacion: 'ml', tipo: 'volumen' },
  u: { nombre: 'Unidades', abreviacion: 'u', tipo: 'unidad' },
  '1': { nombre: 'Unidades', abreviacion: 'u', tipo: 'unidad' }, // Para el caso que creaste
};

// Mapeo inverso: del ID del formulario a la unidad descriptiva
const unidadIdToDescriptiva: Record<string, string> = {
  '1': 'kilogramos',
  '2': 'litros',
  '3': 'unidades',
  '4': 'gramos',
  '5': 'mililitros',
};

// Funciones adaptadoras
const adaptarIngredienteDesdeAPI = (
  ingredienteAPI: IngredienteAPI
): Ingrediente => {
  console.log('🔍 Adaptando ingrediente desde API:', ingredienteAPI);

  // Obtener información de la unidad de medida
  const unidadInfo = unidadesMedidaMap[ingredienteAPI.unidad_medida] || {
    nombre: ingredienteAPI.unidad_medida,
    abreviacion: ingredienteAPI.unidad_medida,
    tipo: 'otro' as const,
  };

  const ingredienteAdaptado: Ingrediente = {
    id: ingredienteAPI.id.toString(),
    nombre: ingredienteAPI.nombre,
    marca: capitalizarMarca(ingredienteAPI.marca),
    descripcion: ingredienteAPI.descripcion,
    categoria: ingredienteAPI.categoria,
    categoriaId: (ingredienteAPI as any).categoria_id, // Agregar ID de categoría
    unidadMedidaId: ingredienteAPI.unidad_medida,
    unidadMedida: {
      id: ingredienteAPI.unidad_medida,
      nombre: unidadInfo.nombre,
      abreviacion: unidadInfo.abreviacion,
      tipo: unidadInfo.tipo,
      activa: true,
    },
    stockMinimo: Number(ingredienteAPI.stock_minimo),
    stockActual: Number(ingredienteAPI.stock_actual),
    stockReservado: 0,
    diasAlertaVencimiento: 7,
    activo: ingredienteAPI.activo,
    fechaCreacion: new Date(ingredienteAPI.created_at),
    fechaActualizacion: new Date(ingredienteAPI.updated_at),
    proveedores: [],
    costoUnitario: Number(ingredienteAPI.costo_unitario || 0), // Agregar costo unitario
    // El backend lo calcula con la misma regla que usa para alertar.
    // Si el endpoint no lo manda (detalle, por ejemplo), se deduce del
    // stock: es la mitad de la regla, y la que se puede saber acá.
    haEstadoEnBodega:
      (ingredienteAPI as { ha_estado_en_bodega?: number | boolean })
        .ha_estado_en_bodega !== undefined
        ? Boolean(
            (ingredienteAPI as { ha_estado_en_bodega?: number | boolean })
              .ha_estado_en_bodega
          )
        : Number(ingredienteAPI.stock_actual || 0) > 0,
  };

  // Si hay información de proveedor, crear un proveedor temporal
  if (ingredienteAPI.proveedor && ingredienteAPI.proveedor_principal_id) {
    const proveedorId = ingredienteAPI.proveedor_principal_id.toString();
    const proveedorTemporal = {
      id: `temp-${ingredienteAPI.id}`,
      ingredienteId: ingredienteAPI.id.toString(),
      proveedorId: proveedorId,
      proveedor: {
        id: proveedorId,
        nombre: ingredienteAPI.proveedor,
        contacto: '',
        email: '',
        telefono: '',
        direccion: '',
        activo: true,
      },
      precioUnitario: Number(ingredienteAPI.costo_unitario || 0),
      tiempoEntrega: 7, // Valor por defecto
      fechaUltimoPrecio: new Date(),
      activo: true,
    };
    ingredienteAdaptado.proveedores = [proveedorTemporal];
  }

  // Preservar el campo proveedor del API como propiedad adicional
  if (ingredienteAPI.proveedor) {
    (ingredienteAdaptado as any).proveedor = ingredienteAPI.proveedor;
  }
  if (ingredienteAPI.proveedor_principal_id) {
    (ingredienteAdaptado as any).proveedor_principal_id =
      ingredienteAPI.proveedor_principal_id;
  }

  return ingredienteAdaptado;
};

const adaptarIngredienteParaAPI = (
  ingrediente: Partial<Ingrediente>
): Partial<CreateIngredienteData> => {
  // Descriptor de la unidad que se manda al backend, que lo resuelve contra
  // `unidades_medida` por simbolo o por nombre.
  //
  // Se prefiere el descriptor del catalogo porque `unidadIdToDescriptiva` es
  // una tabla fija que quedo DESALINEADA de los ids reales: mapea '2'->litros
  // cuando el id 2 es Gramo, '3'->unidades cuando el 3 es Litro, y no cubre
  // los ids 6 al 10. Elegir "Gramo" creaba el ingrediente en litros, y desde
  // el fix de unidades ese texto ademas fija la unidad base con la que se
  // calcula todo el stock. La tabla queda como respaldo para llamadas que no
  // pasan por el formulario.
  const unidadDescriptiva =
    ingrediente.unidadMedida?.abreviacion ||
    ingrediente.unidadMedida?.nombre ||
    (ingrediente.unidadMedidaId
      ? unidadIdToDescriptiva[ingrediente.unidadMedidaId] ||
        ingrediente.unidadMedidaId
      : 'kg');

  // Calcular costo unitario desde proveedores
  let costoUnitario = 0;
  console.log('🔍 Adaptando ingrediente para API:', {
    nombre: ingrediente.nombre,
    proveedores: ingrediente.proveedores,
  });

  // El proveedor principal es el MISMO del que sale el costo unitario.
  let proveedorPrincipalId: number | undefined;

  if (ingrediente.proveedores && ingrediente.proveedores.length > 0) {
    // Usar el precio del primer proveedor activo
    const proveedorActivo = ingrediente.proveedores.find((p) => p.activo);
    const elegido = proveedorActivo ?? ingrediente.proveedores[0];
    costoUnitario = elegido.precioUnitario ?? 0;
    console.log(
      proveedorActivo
        ? '💰 Costo desde proveedor activo:'
        : '💰 Costo desde primer proveedor:',
      costoUnitario
    );

    // `proveedorId` viaja como string en el formulario; la API espera number.
    // Se descartan vacios y no numericos para no mandar NaN.
    const idProveedor = Number(elegido.proveedorId);
    if (Number.isInteger(idProveedor) && idProveedor > 0) {
      proveedorPrincipalId = idProveedor;
    }
  } else {
    console.log('⚠️ No hay proveedores en el ingrediente');
  }

  const result: any = {
    nombre: ingrediente.nombre,
    marca: capitalizarMarca(ingrediente.marca),
    descripcion: ingrediente.descripcion,
    // Enviar categoria_id si está disponible, sino enviar categoria (nombre) para compatibilidad
    ...(ingrediente.categoriaId
      ? { categoria_id: ingrediente.categoriaId }
      : { categoria: ingrediente.categoria }),
    unidad_medida: unidadDescriptiva,
    costo_unitario: costoUnitario, // Usar el costo calculado desde proveedores
    stock_actual: ingrediente.stockActual,
    stock_minimo: ingrediente.stockMinimo,
    stock_maximo: ingrediente.stockActual || 100,
    // Priorizar el campo proveedor directo (del wizard) antes que la estructura compleja
    proveedor:
      (ingrediente as any).proveedor ||
      ingrediente.proveedores?.[0]?.proveedor?.nombre,
    // El nombre de arriba no lo persiste nadie: el esquema de la API solo
    // acepta `proveedor_id` y Zod descarta lo que no conoce. Por eso el
    // proveedor elegido en el alta se perdia siempre, y el ingrediente quedaba
    // sin proveedor principal hasta que se enviaba una orden de compra.
    ...(proveedorPrincipalId ? { proveedor_id: proveedorPrincipalId } : {}),
  };

  // Preservar campos adicionales como forzarCreacion
  if ((ingrediente as any).forzarCreacion !== undefined) {
    result.forzarCreacion = (ingrediente as any).forzarCreacion;
  }

  console.log('📤 Datos finales para API:', result);
  return result;
};

export interface UpdateStockData {
  cantidad: number;
  tipo: 'aumentar' | 'disminuir' | 'establecer';
}

// ===================================================================
// Salida de bodega manual (PROMPT_N°046)
// ===================================================================

export interface EntradaBodegaPayload {
  /** En unidad display del ingrediente. */
  cantidad: number;
  motivo: MotivoEntradaBodega;
  /** YYYY-MM-DD. Si se omite, el backend usa hoy. */
  fecha?: string;
  origen?: string;
  /** Por unidad display. Si se omite, se usa el costo de referencia. */
  costo_unitario?: number;
  proveedor_id?: number;
  fecha_vencimiento?: string;
  notas?: string;
}

export interface EntradaBodegaResultado {
  ingrediente: Ingrediente;
  entrada: {
    cantidad: number;
    cantidad_base: number | null;
    unidad: string;
    motivo_label: string;
    origen: string | null;
    costo_unitario: number;
    costo_era_de_referencia: boolean;
    stock_anterior: number;
    stock_nuevo: number;
    stock_anterior_base: number;
    stock_nuevo_base: number;
  };
  lote: {
    id: number;
    numero_lote: string;
    cantidad: number;
    cantidad_base: number | null;
    fecha_vencimiento: string | null;
  } | null;
}

export interface SalidaBodegaPayload {
  /** En unidad display del ingrediente. */
  cantidad: number;
  motivo: MotivoSalidaBodega;
  /** YYYY-MM-DD. Si se omite, el backend usa hoy. */
  fecha?: string;
  notas?: string;
  destino?: string;
}

export interface SalidaBodegaResumen {
  cantidad: number;
  cantidad_base: number | null;
  unidad: string;
  motivo: MotivoSalidaBodega;
  motivo_label: string;
  destino: string | null;
  notas: string;
  fecha: string | null;
  stock_anterior: number;
  stock_nuevo: number;
  stock_anterior_base: number;
  stock_nuevo_base: number;
  costo_total: number;
}

export interface SalidaBodegaLoteAfectado {
  lote_id: number;
  cantidad_base: number;
  disponible_base_restante: number;
}

export interface ReversoSalidaResultado {
  ingrediente: Ingrediente;
  reverso: {
    movimientos_revertidos: number;
    cantidad_devuelta: number;
    unidad: string;
    motivo: string;
    stock_anterior: number;
    stock_nuevo: number;
  };
  lotes_devueltos: Array<{ lote_id: number; cantidad_base: number }>;
}

export interface SalidaBodegaResultado {
  ingrediente: Ingrediente;
  salida: SalidaBodegaResumen;
  lotes_afectados: SalidaBodegaLoteAfectado[];
}

/** Cuerpo del 409 cuando el stock no alcanza. */
export interface SalidaBodegaConflicto {
  error: string;
  message: string;
  stock_actual: number;
  solicitado: number;
  unidad: string;
}

export const ingredientesService = {
  // Obtener lista de ingredientes
  getAll: async (filters: IngredientesFilters = {}) => {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.categoria) params.append('categoria', filters.categoria);
    if (filters.activo !== undefined)
      params.append('activo', filters.activo.toString());

    const response = await api.get<any>(`/ingredientes?${params}`);

    // El backend devuelve: { success: true, data: [...], pagination: {...} }
    // Necesitamos adaptarlo a: { ingredientes: [...], pagination: {...} }
    const ingredientesAPI = response.data.data || [];
    const paginationAPI = response.data.pagination || {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 1,
    };

    const ingredientesAdaptados = ingredientesAPI.map(
      adaptarIngredienteDesdeAPI
    );

    return {
      ingredientes: ingredientesAdaptados,
      pagination: {
        page: paginationAPI.page,
        limit: paginationAPI.limit,
        total: paginationAPI.total,
        pages: paginationAPI.totalPages, // totalPages en backend → pages en frontend
      },
    };
  },

  // Obtener ingrediente por ID
  getById: async (id: number): Promise<Ingrediente> => {
    const response = await api.get<ApiResponse<IngredienteAPI>>(
      `/ingredientes/${id}`
    );
    return adaptarIngredienteDesdeAPI(response.data.data!);
  },

  // Crear nuevo ingrediente
  //
  // Devuelve `advertenciaUnidad` cuando el backend no pudo resolver la unidad
  // contra el catalogo: el ingrediente SE CREA, pero queda fuera de las
  // conversiones y del consumo por lotes en unidad base. Ese aviso se
  // descartaba aca y el operador no se enteraba. Va como propiedad opcional
  // para no romper a los demas consumidores del servicio.
  create: async (
    ingrediente: Partial<Ingrediente>
  ): Promise<Ingrediente & { advertenciaUnidad?: string }> => {
    const dataAPI = adaptarIngredienteParaAPI(ingrediente);
    const response = await api.post<
      ApiResponse<IngredienteAPI> & { advertencia?: string }
    >('/ingredientes', dataAPI);
    return {
      ...adaptarIngredienteDesdeAPI(response.data.data!),
      advertenciaUnidad: response.data.advertencia,
    };
  },

  // Actualizar ingrediente
  update: async (
    id: number,
    ingrediente: Partial<Ingrediente>
  ): Promise<Ingrediente> => {
    const dataAPI = adaptarIngredienteParaAPI(ingrediente);
    const response = await api.put<ApiResponse<IngredienteAPI>>(
      `/ingredientes/${id}`,
      dataAPI
    );
    return adaptarIngredienteDesdeAPI(response.data.data!);
  },

  // Eliminar ingrediente
  delete: async (id: number): Promise<void> => {
    await api.delete(`/ingredientes/${id}`);
  },

  // Actualizar stock
  updateStock: async (
    id: number,
    stockData: UpdateStockData
  ): Promise<Ingrediente> => {
    const response = await api.patch<ApiResponse<IngredienteAPI>>(
      `/ingredientes/${id}/stock`,
      stockData
    );
    return adaptarIngredienteDesdeAPI(response.data.data!);
  },

  // Obtener categorías disponibles con ID y nombre
  getCategorias: async (): Promise<CategoriaIngrediente[]> => {
    const response = await api.get<ApiResponse<CategoriaIngrediente[]>>(
      '/ingredientes/categorias'
    );
    return response.data.data!;
  },

  // Crear nueva categoría
  createCategoria: async (
    nombre: string,
    descripcion?: string
  ): Promise<CategoriaIngrediente> => {
    const response = await api.post<ApiResponse<CategoriaIngrediente>>(
      '/ingredientes/categorias',
      { nombre, descripcion }
    );
    return response.data.data!;
  },

  // Obtener estadísticas generales de ingredientes
  getEstadisticas: async (): Promise<{
    totalIngredientes: number;
    ingredientesActivos: number;
    alertasStockMinimo: number;
    categoriasCount: Record<string, number>;
  }> => {
    const response = await api.get<
      ApiResponse<{
        totalIngredientes: number;
        ingredientesActivos: number;
        alertasStockMinimo: number;
        categoriasCount: Record<string, number>;
      }>
    >('/ingredientes/estadisticas');
    return response.data.data!;
  },

  // Obtener ingredientes con stock bajo
  getStockBajo: async (): Promise<Ingrediente[]> => {
    const response = await api.get<ApiResponse<IngredienteAPI[]>>(
      '/ingredientes/stock-bajo'
    );
    return response.data.data!.map(adaptarIngredienteDesdeAPI);
  },

  // Deshabilitar ingrediente
  deshabilitar: async (id: number): Promise<Ingrediente> => {
    const response = await api.patch<ApiResponse<IngredienteAPI>>(
      `/ingredientes/${id}/deshabilitar`
    );
    return adaptarIngredienteDesdeAPI(response.data.data!);
  },

  // Buscar ingredientes similares
  buscarSimilares: async (nombre: string): Promise<Ingrediente[]> => {
    const response = await api.get<ApiResponse<IngredienteAPI[]>>(
      `/ingredientes/similares?nombre=${encodeURIComponent(nombre)}`
    );
    return response.data.data!.map(adaptarIngredienteDesdeAPI);
  },

  // Reactivar ingrediente
  reactivar: async (id: number, stockActual: number): Promise<Ingrediente> => {
    const response = await api.patch<ApiResponse<IngredienteAPI>>(
      `/ingredientes/${id}/reactivar`,
      { stock_actual: stockActual }
    );
    return adaptarIngredienteDesdeAPI(response.data.data!);
  },

  // Ajustar stock manualmente
  ajustarStock: async (
    id: number,
    data: { stock_nuevo: number; motivo: string; notas?: string }
  ): Promise<{
    ingrediente: Ingrediente;
    movimiento: {
      tipo: string;
      stock_anterior: number;
      stock_nuevo: number;
      diferencia: number;
      motivo: string;
    };
  }> => {
    const response = await api.patch<
      ApiResponse<{
        ingrediente: IngredienteAPI;
        movimiento: {
          tipo: string;
          stock_anterior: number;
          stock_nuevo: number;
          diferencia: number;
          motivo: string;
        };
      }>
    >(`/ingredientes/${id}/ajustar-stock`, data);

    return {
      ingrediente: adaptarIngredienteDesdeAPI(response.data.data!.ingrediente),
      movimiento: response.data.data!.movimiento,
    };
  },

  // Registrar una salida de bodega manual (PROMPT_N°046).
  // `cantidad` va en la unidad display del ingrediente; el backend la
  // convierte a unidad base y consume lotes en orden FEFO.
  registrarSalida: async (
    id: number,
    data: SalidaBodegaPayload
  ): Promise<SalidaBodegaResultado> => {
    const response = await api.post<
      ApiResponse<{
        ingrediente: IngredienteAPI;
        salida: SalidaBodegaResumen;
        lotes_afectados: SalidaBodegaLoteAfectado[];
      }>
    >(`/ingredientes/${id}/salida`, data);

    return {
      ingrediente: adaptarIngredienteDesdeAPI(response.data.data!.ingrediente),
      salida: response.data.data!.salida,
      lotes_afectados: response.data.data!.lotes_afectados,
    };
  },

  // Registrar una entrada de bodega manual (PROMPT_N°052). Crea un lote
  // nuevo y suma stock; NO toca el costo de referencia del ingrediente.
  registrarEntrada: async (
    id: number,
    data: EntradaBodegaPayload
  ): Promise<EntradaBodegaResultado> => {
    const response = await api.post<
      ApiResponse<{
        ingrediente: IngredienteAPI;
        entrada: EntradaBodegaResultado['entrada'];
        lote: EntradaBodegaResultado['lote'];
      }>
    >(`/ingredientes/${id}/entrada`, data);

    return {
      ingrediente: adaptarIngredienteDesdeAPI(response.data.data!.ingrediente),
      entrada: response.data.data!.entrada,
      lote: response.data.data!.lote,
    };
  },

  // Deshacer una salida de bodega (PROMPT_N°051). No borra: compensa con
  // un movimiento de entrada que devuelve lo que la salida se llevó.
  revertirSalida: async (
    id: number,
    movimientoId: number,
    motivo: string
  ): Promise<ReversoSalidaResultado> => {
    const response = await api.post<
      ApiResponse<{
        ingrediente: IngredienteAPI;
        reverso: ReversoSalidaResultado['reverso'];
        lotes_devueltos: ReversoSalidaResultado['lotes_devueltos'];
      }>
    >(`/ingredientes/${id}/salida/${movimientoId}/revertir`, { motivo });

    return {
      ingrediente: adaptarIngredienteDesdeAPI(response.data.data!.ingrediente),
      reverso: response.data.data!.reverso,
      lotes_devueltos: response.data.data!.lotes_devueltos,
    };
  },

  // Obtener historial de movimientos de stock
  getMovimientos: async (
    id: number,
    filters?: MovimientosFilters
  ): Promise<MovimientosResponse> => {
    const params = new URLSearchParams();

    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.tipo) params.append('tipo', filters.tipo);
    if (filters?.motivo) params.append('motivo', filters.motivo);
    if (filters?.fecha_inicio)
      params.append('fecha_inicio', filters.fecha_inicio);
    if (filters?.fecha_fin) params.append('fecha_fin', filters.fecha_fin);

    const queryString = params.toString();
    const url = `/ingredientes/${id}/movimientos${queryString ? `?${queryString}` : ''}`;

    const response = await api.get<ApiResponse<MovimientosResponse>>(url);
    return response.data.data!;
  },

  // ==================================================================
  // Sistema de unidades base / lotes (Fase 1-2, abril 2026)
  // ==================================================================

  getLotes: async (id: number): Promise<LotesIngredienteResponse> => {
    const response = await api.get<ApiResponse<LotesIngredienteResponse>>(
      `/ingredientes/${id}/lotes`
    );
    return response.data.data!;
  },

  getWarningsUnidades: async (): Promise<WarningUnidades[]> => {
    const response = await api.get<{ success: boolean; data: WarningUnidades[] }>(
      '/ingredientes/warnings'
    );
    return response.data.data;
  },

  resolverWarning: async (warningId: number): Promise<void> => {
    await api.patch(`/ingredientes/warnings/${warningId}/resolver`);
  },

  getInventarioBodega: async (): Promise<InventarioBodegaResponse> => {
    const response = await api.get<ApiResponse<InventarioBodegaResponse>>(
      '/ingredientes/inventario-bodega'
    );
    return response.data.data!;
  },
};

// ==================================================================
// Tipos para lotes y warnings
// ==================================================================

export interface LoteIngrediente {
  id: number;
  numero_lote: string;
  fecha_ingreso: string;
  fecha_vencimiento: string | null;
  cantidad: number;
  cantidad_base: number;
  cantidad_disponible: number;
  cantidad_disponible_base: number;
  precio_unitario: number;
  costo_unitario_base: number;
  formato_compra: string | null;
  cantidad_packs: number | null;
  unidades_por_pack: number | null;
  es_pack: boolean;
  unidad_pack_simbolo: string | null;
  proveedor_nombre: string | null;
  numero_factura: string | null;
  valorizacion_lote: number;
  /** El costo salió del catálogo porque el lote no traía el suyo (N°055). */
  a_precio_referencia: boolean;
  estado: 'disponible' | 'proximo_vencer' | 'vencido' | 'agotado';
}

export interface LotesIngredienteResponse {
  ingrediente: {
    id: number;
    nombre: string;
    tipo_fisico: 'peso' | 'volumen' | 'cantidad' | null;
    unidad_base_simbolo: string | null;
    unidad_display_simbolo: string | null;
    stock_actual: number;
    stock_actual_base: number;
  };
  lotes: LoteIngrediente[];
  totales: {
    total_disponible_base: number;
    /** Lotes + stock sin lote, todo al criterio de costo de lote (N°055). */
    valorizacion_total: number;
    costo_promedio_base: number;
    lotes_a_referencia: number;
    stock_sin_lote: number;
    costo_referencia: number;
  };
}

export interface WarningUnidades {
  id: number;
  entidad: 'ingrediente' | 'factura_compra' | string;
  entidad_id: number | null;
  tipo_warning: string;
  detalle: string;
  created_at: string;
  referencia: string | null;
}

export interface BodegaIngredienteRow {
  id: number;
  nombre: string;
  categoria: string | null;
  activo: boolean;
  stock_actual: number;
  stock_actual_base: number;
  stock_minimo: number;
  stock_minimo_base: number;
  tipo_fisico: 'peso' | 'volumen' | 'cantidad' | null;
  unidad_base_simbolo: string | null;
  unidad_display_simbolo: string | null;
  lotes_activos: number;
  total_disponible_base: number;
  valorizacion_total: number;
  costo_promedio_base: number;
  lotes_a_referencia: number;
  stock_sin_lote: number;
  lotes_por_vencer: number;
  lotes_vencidos: number;
  proximo_vencimiento: string | null;
  estado_stock: 'ok' | 'bajo' | 'agotado';
}

export interface InventarioBodegaTotales {
  valorizacion_total: number;
  lotes_activos: number;
  lotes_a_referencia: number;
  ingredientes_stock_sin_lote: number;
  lotes_por_vencer: number;
  lotes_vencidos: number;
  ingredientes_stock_bajo: number;
  ingredientes_agotados: number;
  total_ingredientes: number;
}

export interface InventarioBodegaResponse {
  ingredientes: BodegaIngredienteRow[];
  totales: InventarioBodegaTotales;
}

export default ingredientesService;
